import { useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Sparkles, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function errDetail(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

function ArtSlot({ label, ratio, url, busy, onGenerate, onClear, testId, hint }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <div className={`relative overflow-hidden rounded-xl border border-border bg-secondary ${ratio}`}>
        {url ? (
          <img src={url} alt={label} className="h-full w-full object-cover" data-testid={`${testId}-image`} />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">{hint}</div>
        )}
        {busy && <div className="absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-sm"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
        {url && !busy && (
          <button type="button" onClick={onClear} className="absolute right-1.5 top-1.5 rounded-full bg-background/90 p-1 text-muted-foreground shadow hover:text-destructive" aria-label={`Remove ${label}`} data-testid={`${testId}-clear`}><X className="h-3.5 w-3.5" /></button>
        )}
      </div>
      <div className="mt-2 flex flex-col items-start gap-1.5">
        <span className="text-xs font-medium text-foreground">{label}</span>
        <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={onGenerate} disabled={busy} data-testid={`${testId}-generate`}>
          <Sparkles className="mr-1 h-3 w-3" />{url ? "Regenerate" : "Generate"}
        </Button>
      </div>
    </div>
  );
}

/** Admin → Locations: fal.ai theme panel + venue postcard for one location. */
export function LocationArt({ loc, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [hint, setHint] = useState("");
  const [uploading, setUploading] = useState(false);

  const run = async (kind) => {
    setBusy(kind);
    try {
      await api.post(`/art/${kind}/${loc.location_id}`, { style_hint: hint || null });
      toast.success(kind === "theme" ? "Theme art ready" : "Postcard ready");
      onChanged?.();
    } catch (e) {
      toast.error(errDetail(e, "Generation failed — try again"));
    } finally { setBusy(null); }
  };

  const clear = async (kind) => {
    try { await api.delete(`/art/${kind}/${loc.location_id}`); onChanged?.(); } catch (e) { toast.error("Could not remove"); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", "venue-photos");
      const res = await api.post("/storage/upload", fd);
      await api.put(`/locations/${loc.location_id}`, { ...loc, photo: res.data.url });
      toast.success("Venue photo saved — postcards will use it as the reference");
      onChanged?.();
    } catch (e) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="mt-4 rounded-xl border border-dashed border-border bg-secondary/40 p-3" data-testid={`location-art-${loc.location_id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary"><Sparkles className="h-3.5 w-3.5" /> AI art (fal.ai)</p>
        <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" asChild>
          <label className="cursor-pointer" data-testid={`location-photo-upload-${loc.location_id}`}>
            {uploading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Camera className="mr-1 h-3 w-3" />}
            {loc.photo ? "Replace venue photo" : "Add venue photo"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        </Button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <ArtSlot label="Theme panel" ratio="aspect-video" url={loc.theme_art} busy={busy === "theme"} onGenerate={() => run("theme")} onClear={() => clear("theme")} testId={`theme-art-${loc.location_id}`} hint="Abstract art for the topic card + hero" />
        <ArtSlot label="Venue postcard" ratio="aspect-[3/2]" url={loc.postcard_image} busy={busy === "postcard"} onGenerate={() => run("postcard")} onClear={() => clear("postcard")} testId={`postcard-${loc.location_id}`} hint={loc.photo ? "Illustrated from your venue photo" : "Illustrated from the address"} />
      </div>
      <Input value={hint} onChange={(e) => setHint(e.target.value)} placeholder="Art direction (optional) — e.g. autumn light, more teal" className="mt-3 h-8 text-xs" data-testid={`art-hint-${loc.location_id}`} />
    </div>
  );
}

export default LocationArt;
